import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { useProducts } from "@/lib/useProducts";
import { useSeo } from "@/lib/seo";
import { useLang, lp, loc } from "@/lib/i18n";

const T = {
  fr: {
    seoTitle: "Maroquinerie — Maison Partita",
    seoDesc: "Sacs à main pour femme, cuirs tannés en Toscane, ferrures dorées, faits en petites séries numérotées.",
    kicker: "La Maison · Cuir",
    title: "Maroquinerie",
    intro: "Le cuir de la maison. Sacs à main structurés et souples, cuirs pleine fleur tannés en Toscane, ferrures en laiton doré à l'or fin. Faits main, en petites séries numérotées.",
    theBags: "Les sacs",
    altBag: (name, roman, material) => `Sac ${name} — Maroquinerie ${roman}, ${material}`,
  },
  en: {
    seoTitle: "Leather Goods — Maison Partita",
    seoDesc: "Women's handbags, Tuscan-tanned leathers, gilded hardware, made in small numbered series.",
    kicker: "The House · Leather",
    title: "Leather Goods",
    intro: "The house's leather. Structured and supple handbags, full-grain leathers tanned in Tuscany, hardware in brass gilded with fine gold. Handmade, in small numbered series.",
    theBags: "The bags",
    altBag: (name, roman, material) => `${name} bag — Leather Goods ${roman}, ${material}`,
  },
};

export default function Maroquinerie() {
  const lang = useLang();
  const t = T[lang];
  useSeo(t.seoTitle, t.seoDesc);
  const { products } = useProducts("maroquinerie");

  return (
    <div>
      <section className="max-w-3xl mx-auto px-5 sm:px-10 pt-28 pb-20">
        <Reveal>
          <p className="text-[13px] tracking-[0.34em] uppercase text-zinc-500">{t.kicker}</p>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl tracking-[0.1em] uppercase text-zinc-50">{t.title}</h1>
          <p className="mt-10 text-[20px] leading-[1.8] text-zinc-300">{t.intro}</p>
        </Reveal>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-10 pb-32">
        <Reveal><p className="text-[13px] tracking-[0.34em] uppercase text-zinc-500 mb-16">{t.theBags}</p></Reveal>
        <div className="grid gap-x-10 gap-y-20 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={(i % 3) * 0.12}>
              <Link to={lp(lang, `/maroquinerie/${p.slug}`)} data-testid={`maroquinerie-${p.slug}`} className="group block">
                <div className="overflow-hidden bg-zinc-900 border border-zinc-800">
                  <img src={p.photo} alt={t.altBag(p.name, p.roman, loc(p, lang, "material"))}
                    width="848" height="1264" loading="lazy" decoding="async"
                    className="w-full h-auto transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
                    style={{ aspectRatio: "4/5", objectFit: "cover" }} />
                </div>
                <div className="mt-6">
                  <div className="flex items-baseline justify-between gap-3">
                    <h3 className="text-2xl tracking-[0.06em] text-zinc-100">{p.name}</h3>
                    <span className="text-[13px] tracking-[0.2em] uppercase text-zinc-600">{p.roman}</span>
                  </div>
                  <p className="mt-2 text-zinc-300">{p.price} €</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
